/**
 * Unified Dashboard Component
 * Displays Stats Overview and PM Tasks side-by-side in compact cards
 */

import React from 'react';
import { AlertTriangle, TrendingUp, CheckCircle, XCircle, Activity, Repeat, Clock, MapPin, Users } from 'lucide-react';
import { AlertStats } from '../types';
import { PMTask } from '../hooks/usePMTasks';

interface UnifiedDashboardProps {
  activeTab?: string;
  stats: AlertStats | null;
  pmTasks: PMTask[];
  pmTasksLoading: boolean;
  onStatClick?: (filterType: string, filterValue: string) => void;
}

export const UnifiedDashboard: React.FC<UnifiedDashboardProps> = ({ 
  activeTab = 'dashboard-overview',
  stats, 
  pmTasks, 
  pmTasksLoading,
  onStatClick 
}) => {
  const handleStatClick = (filterType: string, filterValue: string) => {
    if (onStatClick) {
      onStatClick(filterType, filterValue);
    }
  };

  // Calculate PM task stats
  const pmStats = {
    total: pmTasks.length,
    scheduled: pmTasks.filter(t => t.status === 'scheduled').length,
    inProgress: pmTasks.filter(t => t.status === 'in-progress').length,
    completed: pmTasks.filter(t => t.status === 'completed').length
  };

  return (
    <div className="unified-dashboard">

      {/* Single Row Grid - Stats and PM Tasks */}
      <div className="dashboard-row">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="section-header">
            {activeTab === 'pm-calendar' ? <Activity size={14} /> : <AlertTriangle size={14} />}
            <span>Statistics Overview</span>
          </div>
          <div className="compact-stats-grid">
            {activeTab === 'pm-calendar' ? (
              <>
                <div className="compact-stat-card stat-total">
                  <div className="stat-value">{pmStats.total}</div>
                  <div className="stat-label">Total PM Tasks</div>
                </div>
                <div className="compact-stat-card stat-medium">
                  <div className="stat-value">{pmStats.scheduled}</div>
                  <div className="stat-label">Scheduled</div>
                </div>
                <div className="compact-stat-card stat-low">
                  <div className="stat-value">{pmStats.inProgress}</div>
                  <div className="stat-label">In Progress</div>
                </div>
                <div className="compact-stat-card stat-success">
                  <div className="stat-value">{pmStats.completed}</div>
                  <div className="stat-label">Completed</div>
                </div>
              </>
            ) : (
              <>
                <div 
                  className="compact-stat-card stat-high" 
                  onClick={() => handleStatClick('priority', 'high')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.high_priority || 0}</div>
                  <div className="stat-label">High</div>
                </div>

                <div 
                  className="compact-stat-card stat-medium" 
                  onClick={() => handleStatClick('priority', 'medium')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.medium_priority || 0}</div>
                  <div className="stat-label">Medium</div>
                </div>

                <div 
                  className="compact-stat-card stat-low" 
                  onClick={() => handleStatClick('priority', 'low')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.low_priority || 0}</div>
                  <div className="stat-label">Low</div>
                </div>

                <div className="compact-stat-card stat-total">
                  <div className="stat-value">{stats?.total_alerts || 0}</div>
                  <div className="stat-label">Total</div>
                </div>

                <div 
                  className="compact-stat-card stat-success" 
                  onClick={() => handleStatClick('rectification', 'rectified')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.rectified_count || 0}</div>
                  <div className="stat-label">Rectified</div>
                </div>

                <div 
                  className="compact-stat-card stat-error" 
                  onClick={() => handleStatClick('rectification', 'not rectified')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.not_rectified_count || 0}</div>
                  <div className="stat-label">Pending</div>
                </div>

                <div 
                  className="compact-stat-card stat-success" 
                  onClick={() => handleStatClick('accuracy', 'true')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.accurate_alerts || 0}</div>
                  <div className="stat-label">Accurate</div>
                </div>

                <div 
                  className="compact-stat-card stat-error" 
                  onClick={() => handleStatClick('accuracy', 'false')}
                  title="Click to filter"
                >
                  <div className="stat-value">{stats?.inaccurate_alerts || 0}</div>
                  <div className="stat-label">Inaccurate</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .unified-dashboard {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0;
        }

        .dashboard-title {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .dashboard-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          min-height: auto;
        }

        .stats-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .section-header svg {
          color: #0f172a;
        }

        .compact-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
          gap: 8px;
        }

        .compact-stat-card {
          padding: 12px 8px;
          border-radius: 6px;
          text-align: center;
          border: 1px solid;
          transition: all 0.2s ease;
          cursor: pointer;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .compact-stat-card:hover {
          transform: translateY(-2px);
        }

        .compact-stat-card.stat-high {
          background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%);
          border-color: #fca5a5;
        }

        .compact-stat-card.stat-medium {
          background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
          border-color: #fde047;
        }

        .compact-stat-card.stat-low {
          background: linear-gradient(135deg, #E3F0F9 0%, #F2F8FC 100%);
          border-color: #8AC4E6;
        }

        .compact-stat-card.stat-total {
          background: linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%);
          border-color: #C2DFF1;
        }

        .compact-stat-card.stat-success {
          background: linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%);
          border-color: #86efac;
        }

        .compact-stat-card.stat-error {
          background: linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%);
          border-color: #cbd5e1;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2px;
          font-family: 'IBM Plex Sans', sans-serif;
        }

        .stat-label {
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          font-family: 'IBM Plex Sans', sans-serif;
        }
      `}} />
    </div>
  );
};
